import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiCopy } from 'react-icons/fi';
import {
  fetchWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  regenerateSecret
} from '../redux/slices/webhookSlice';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Pagination from '../components/common/Pagination';
import projectService from '../services/projectService';
import { toast } from 'react-toastify';
import './Webhooks.css';

const initialFormState = {
  project_id: '',
  name: '',
  url: '',
  events: ['transaction.approved', 'transaction.rejected'],
  retry_count: 3,
  timeout_ms: 30000
};

const Webhooks = () => {
  const dispatch = useDispatch();
  const { webhooks, pagination, loading } = useSelector((state) => state.webhooks);

  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [formData, setFormData] = useState(initialFormState);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSecretConfirm, setShowSecretConfirm] = useState(false);
  const [projectFilter, setProjectFilter] = useState('');

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await projectService.getProjects();
        setProjects(response.data || []);
      } catch (error) {
        console.error('Failed to load projects:', error);
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    dispatch(fetchWebhooks({ project_id: projectFilter || undefined }));
  }, [dispatch, projectFilter]);

  const handleOpenCreate = () => {
    setFormData(initialFormState);
    setModalMode('create');
    setShowModal(true);
  };

  const handleOpenEdit = (webhook) => {
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      is_active: webhook.is_active,
      retry_count: webhook.retry_count,
      timeout_ms: webhook.timeout_ms
    });
    setSelectedWebhook(webhook);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (modalMode === 'create') {
        await dispatch(createWebhook(formData)).unwrap();
        toast.success('Webhook created successfully');
      } else {
        await dispatch(
          updateWebhook({ id: selectedWebhook.id, data: formData })
        ).unwrap();
        toast.success('Webhook updated successfully');
      }
      setShowModal(false);
      setFormData(initialFormState);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteWebhook(selectedWebhook.id)).unwrap();
      toast.success('Webhook deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedWebhook(null);
    } catch (error) {
      toast.error(error);
    }
  };

  const handleRegenerateSecret = async () => {
    try {
      const result = await dispatch(regenerateSecret(selectedWebhook.id)).unwrap();
      toast.success('Secret key regenerated');
      setShowSecretConfirm(false);

      // Copy to clipboard
      navigator.clipboard.writeText(result.secret_key);
      toast.info('New secret copied to clipboard');
    } catch (error) {
      toast.error(error);
    }
  };

  const copySecret = (secret) => {
    navigator.clipboard.writeText(secret);
    toast.success('Secret key copied to clipboard');
  };

  const projectOptions = [
    { value: '', label: 'Select Project' },
    ...projects.map(p => ({ value: p.id.toString(), label: p.display_name }))
  ];

  const columns = [
    {
      key: 'name',
      title: 'Name',
      render: (value) => <span className="webhook-name">{value}</span>
    },
    {
      key: 'url',
      title: 'URL',
      render: (value) => (
        <span className="webhook-url" title={value}>
          {value.length > 40 ? value.substring(0, 40) + '...' : value}
        </span>
      )
    },
    {
      key: 'project',
      title: 'Project',
      render: (_, row) => <Badge variant="default">{row.project?.display_name || '-'}</Badge>
    },
    {
      key: 'events',
      title: 'Events',
      render: (value) => (
        <div className="events-list">
          {value?.map((event) => (
            <Badge key={event} variant="info" size="small">
              {event}
            </Badge>
          ))}
        </div>
      )
    },
    {
      key: 'is_active',
      title: 'Status',
      render: (value) => (
        <Badge variant={value ? 'success' : 'danger'}>
          {value ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      key: 'secret_key',
      title: 'Secret',
      render: (value) => (
        <div className="secret-cell">
          <span className="secret-masked">••••••••</span>
          <button
            className="copy-btn"
            onClick={() => copySecret(value)}
            title="Copy Secret"
          >
            <FiCopy size={14} />
          </button>
        </div>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <button
            className="action-btn edit"
            onClick={() => handleOpenEdit(row)}
            title="Edit"
          >
            <FiEdit2 />
          </button>
          <button
            className="action-btn reset"
            onClick={() => {
              setSelectedWebhook(row);
              setShowSecretConfirm(true);
            }}
            title="Regenerate Secret"
          >
            <FiRefreshCw />
          </button>
          <button
            className="action-btn delete"
            onClick={() => {
              setSelectedWebhook(row);
              setShowDeleteConfirm(true);
            }}
            title="Delete"
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <Layout title="Webhooks">
      <div className="webhooks-page">
        <Card
          title="Webhook Management"
          subtitle="Configure callback endpoints for transaction updates"
          actions={
            <div className="card-actions-row">
              <select
                className="project-filter-select"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.display_name}</option>
                ))}
              </select>
              <Button onClick={handleOpenCreate} size="small">
                <FiPlus /> Add Webhook
              </Button>
            </div>
          }
        >
          <Table
            columns={columns}
            data={webhooks}
            loading={loading}
            emptyMessage="No webhooks configured"
          />

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={(page) =>
              dispatch(fetchWebhooks({ project: projectFilter || undefined, page }))
            }
          />
        </Card>

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={modalMode === 'create' ? 'Create Webhook' : 'Edit Webhook'}
          size="medium"
        >
          <form onSubmit={handleSubmit} className="webhook-form">
            {modalMode === 'create' && (
              <Select
                label="Project"
                name="project_id"
                value={formData.project_id}
                onChange={(e) =>
                  setFormData({ ...formData, project_id: e.target.value })
                }
                options={projectOptions}
                required
              />
            )}

            <Input
              label="Webhook Name"
              name="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Production Callback"
              required
            />

            <Input
              label="Webhook URL"
              name="url"
              value={formData.url}
              onChange={(e) =>
                setFormData({ ...formData, url: e.target.value })
              }
              placeholder="https://example.com/webhook"
              required
            />

            <Input
              type="number"
              label="Retry Count"
              name="retry_count"
              value={formData.retry_count}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  retry_count: parseInt(e.target.value)
                })
              }
            />

            <Input
              type="number"
              label="Timeout (ms)"
              name="timeout_ms"
              value={formData.timeout_ms}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  timeout_ms: parseInt(e.target.value)
                })
              }
            />

            {modalMode === 'edit' && (
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            )}

            <div className="form-actions">
              <Button
                variant="secondary"
                type="button"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={loading}>
                {modalMode === 'create' ? 'Create Webhook' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Webhook"
          message={`Are you sure you want to delete "${selectedWebhook?.name}"? This will also delete all associated logs.`}
          confirmText="Delete"
          loading={loading}
        />

        {/* Regenerate Secret Confirmation */}
        <ConfirmDialog
          isOpen={showSecretConfirm}
          onClose={() => setShowSecretConfirm(false)}
          onConfirm={handleRegenerateSecret}
          title="Regenerate Secret Key"
          message="Are you sure you want to regenerate the secret key? The old key will no longer work for signature verification."
          confirmText="Regenerate"
          variant="warning"
          loading={loading}
        />
      </div>
    </Layout>
  );
};

export default Webhooks;
